import {Injectable} from '@angular/core';
import {LoggerService} from 'app/original-services/logger.service';
import {TranslateService} from '@ngx-translate/core';
import moment from 'moment';
import { StorageService } from './storage.service';
import * as dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/it';
// Keep these STATIC. moment loads locales itself via a dynamic `require('./locale/' + name)`
// (moment.js, loadLocale), which the esbuild-based application builder cannot resolve — the call
// throws, moment swallows it and marks the locale "not found", and moment.locale('de') then
// silently leaves everything in English. Karma still builds with webpack, which does resolve that
// require, so the unit tests pass either way and cannot catch a missing import here.
import 'moment/locale/de';
import 'moment/locale/it';

// Each moment locale file registers itself with moment.defineLocale(), which — like
// updateLocale() — ALSO switches the global locale as a backwards-compat side effect. The imports
// above therefore leave moment on Italian. Put it back on its default; setup() below installs the
// real one during app init.
moment.locale('en');

@Injectable({ providedIn: 'root' })
export class LanguageService {
    constructor(private translate: TranslateService,
                private storage: StorageService,
                private logger: LoggerService) {
    }

    defaultLang = 'de';
    setup() {
        if (this.storage?.language) {
            this.setupFromStorage();
            return;
        }
        // this language will be used as a fallback when a translation isn't found in the current language
        this.translate.setFallbackLang(this.defaultLang);

        // the lang to use, if the lang isn't available, it will use the current loader to get them
        let browserLang = this.translate.getBrowserLang();
        this.logger.log('Browser language: ' + browserLang);
        let locale = browserLang.match(/en|de|it/) ? browserLang : this.defaultLang;
        this.translate.use(locale);
        this.setDateLocales(locale || this.defaultLang);
    }

    setupFromStorage() {
        let language = this.storage.language;
        this.logger.log('Storage language: ' + language);
        let locale = language.match(/en|de|it/) ? language : this.defaultLang;
        this.setDateLocales(locale);

        return this.translate.use(locale);
    }

    // Single place where the date libraries are pointed at the chosen language.
    //
    // The app shows Monday-start weeks everywhere. moment's German and Italian locales
    // already do that; only English defaults to Sunday, so English alone needs patching.
    // That patch MUST stay scoped to English: moment.updateLocale() also switches the
    // global locale as a documented side effect ("backwards compat for now: also set the
    // locale"), so calling it while running German would silently drop moment back to
    // English month names for the rest of the session.
    private setDateLocales(locale: string): void {
        if (locale.startsWith('en')) {
            moment.updateLocale('en', {
                week: {
                    dow: 1,
                    doy: 7
                }
            });
        }
        // moment.locale() returns the locale that actually took effect, and falls back to the
        // previous one when the requested locale data is missing. Without this check that failure
        // is invisible: dates just stay English everywhere.
        const applied = moment.locale(locale);
        if (!locale.toLowerCase().startsWith(applied.toLowerCase())) {
            this.logger.warning(`moment locale '${locale}' did not apply (still '${applied}') `
                + `— is 'moment/locale/${locale}' imported in language.service.ts?`);
        }
        dayjs.locale(locale);
    }

    getLocale() {
        let lang = this.storage.language || this.translate.getBrowserCultureLang();
        if (!lang) {
            return 'de-DE';
        }
        if (lang.indexOf('de') === 0) return 'de-DE';
        if (lang.indexOf('it') === 0) return 'it-IT';
        if (lang.indexOf('en') === 0) return 'en-US';
        return 'de-DE'; // unrecognized/future language code — default to the app default
    }

    getDecimalSeparator(locale) {
        const numberWithDecimalSeparator = 1.1;
        return Intl.NumberFormat(locale)
           .formatToParts(numberWithDecimalSeparator)
           .find(part => part.type === 'decimal')
           .value;
     }

    /**
     * Returns the browser language
     * @returns The browser language code (e.g., 'en', 'de', 'it')
     */
    getBrowserLanguage(): string {
        return this.translate.getBrowserLang();
    }
}
